import { useEffect, useState } from 'react';
import { Button, Form, FormInstance, Input, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReviewAction } from '../../shared/Constants';
import Icon from '../../components/ui-components/Icon';
import GreenTick from '../../assets/images/green-tick.svg';
import GrayTick from '../../assets/images/gray-tick.svg';
import DivideCircle from '../../assets/images/divide-circle.svg';
import RedDivideCircle from '../../assets/images/red-divide-circle.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;
export interface FeedbackComponentUIConfig {
	form?: FormInstance;
	commentsLabel?: string;
	commentsMandatory?: boolean;
	disabled?: boolean;
}

type ReviewButtonStyleParams = {
	selectedReview: string | null;
	currentReview: string;
	disabled: boolean;
	baseClass: string;
	selectedBgClass: string;
	borderColorClass: string;
	hoverClass?: string;
};

const getReviewButtonClass = ({
	selectedReview,
	currentReview,
	disabled,
	baseClass,
	selectedBgClass,
	borderColorClass,
	hoverClass = '',
}: ReviewButtonStyleParams) => {
	const isSelected = selectedReview === currentReview;
	const opacity = disabled && !isSelected ? 'opacity-50' : 'opacity-100';
	const cursor = disabled ? 'cursor-not-allowed' : 'cursor-pointer';
	const bgClass = isSelected ? selectedBgClass : 'bg-transparent';

	return `
    ${baseClass}
    border border-solid ${borderColorClass}
    ${hoverClass}
    ${bgClass}
    ${opacity}
    ${cursor}
  `.trim();
};

export const FeedbackComponent = ({
	form,
	commentsLabel = 'comments',
	commentsMandatory = false,
	disabled = false,
}: FeedbackComponentUIConfig) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language == 'ta' || i18n.language == 'ma' || i18n.language == 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [selectedReview, setSelectedReview] = useState<string | null>(null);
	const [commentsRequired, setCommentsRequired] =
		useState<boolean>(commentsMandatory);

	const reviewValue = Form.useWatch('review', form) as string;

	useEffect(() => {
		if (reviewValue) {
			setSelectedReview(reviewValue);
		}
	}, [reviewValue]);

	const handleReviewChange = (value: string) => {
		setSelectedReview(value);
		if (form) {
			form.setFieldsValue({ review: value });
		}

		// If comments are mandatory, enforce the required validation
		setCommentsRequired(commentsMandatory);
	};

	const buttonProps =
		'w-44 h-9 font-sans font-bold text-[13px] text-gray-650 hover:!text-gray-650';

	return (
		<div className="flex flex-row items-start">
			<div className="flex flex-col w-full">
				{/* Conditionally render comments section */}
				{commentsMandatory && (
					<>
						<Text className="mt-2 font-sans font-normal text-sm text-black-100">
							{t(commentsLabel)}{' '}
							{commentsRequired && (
								<span className="text-red-500">
									<b>*</b>
								</span>
							)}
						</Text>
						<Form.Item
							className="font-sans font-bold"
							name="comments"
							rules={[
								{
									required: commentsRequired,
									message: <span>{t('required')}</span>,
								},
								{
									min: 10,
									message: <span>{t('commentsShouldBeClear')}</span>,
								},
							]}
						>
							<Input.TextArea
								showCount
								rows={4}
								placeholder={
									commentsRequired ? `${t('appropirateComments')}` : ''
								}
								className="font-sans text-sm font-normal"
								disabled={disabled}
							/>
						</Form.Item>
					</>
				)}

				{/* Review selection buttons */}
				<Form.Item
					name="review"
					rules={[
						{ required: true, message: <span>{t('answerIsRequired')}</span> },
					]}
				>
					<div
						className={`${
							isStackVertical
								? 'flex flex-col items-center '
								: 'flex justify-center gap-4'
						}py-4 gap-4`}
					>
						<Button
							icon={
								<Icon
									src={
										selectedReview === ReviewAction.REJECT
											? RedDivideCircle
											: DivideCircle
									}
									width="w-5"
									height="h-5"
								/>
							}
							onClick={() => handleReviewChange(ReviewAction.REJECT)}
							iconPosition="end"
							className={getReviewButtonClass({
								selectedReview,
								currentReview: ReviewAction.REJECT,
								disabled,
								baseClass: buttonProps,
								selectedBgClass: '!bg-ngf-orange',
								borderColorClass: '!border-ngf-border-orange',
								hoverClass: 'hover:!bg-ngf-orange',
							})}
							disabled={disabled}
						>
							{t('notGoingForward')}
						</Button>
						<Button
							icon={
								<Icon
									src={
										selectedReview === ReviewAction.APPROVE
											? GreenTick
											: GrayTick
									}
									width="w-5"
									height="h-5"
								/>
							}
							disabled={disabled}
							onClick={() => handleReviewChange(ReviewAction.APPROVE)}
							iconPosition="end"
							className={getReviewButtonClass({
								selectedReview,
								currentReview: ReviewAction.APPROVE,
								disabled,
								baseClass: buttonProps,
								selectedBgClass: '!bg-approve-green',
								borderColorClass: '!border-approve-border-green',
								hoverClass: 'hover:!text-gray-650 hover:!bg-approve-green',
							})}
						>
							{t('approve')}
						</Button>
					</div>
				</Form.Item>
			</div>
		</div>
	);
};
